import { createContextHook } from '@upheal/utils/hooks/createContextHook';
import { createContext, PropsWithChildren } from 'react';
import { GQLContractWithoutClientConsentsAndClientOverviewFragment } from '@upheal/graphql/definitions/contracts/ContractWithoutClientConsentsAndClientOverviewFragment.generated';
import { DateTime } from 'luxon';

/**
 * Dependency Injection for Video Session Components
 *
 * This context allows the telehealth library to use components from other modules
 * (primarily apps/upheal-app) without creating circular dependencies at the
 * TypeScript module level.
 *
 * Instead of importing components directly (which would create lib -> app dependency),
 * the app injects them via this provider, keeping the dependency direction correct:
 * app -> lib (not lib -> app).
 */

type Dependencies = {
    BookSession: React.ComponentType<{
        open: boolean;
        onClose: () => void;
        contract?: GQLContractWithoutClientConsentsAndClientOverviewFragment;
        onBookedSession?: (bookedDate: DateTime) => void;
    }>;
};

const VideoSessionDependencyInjectionContext = createContext<
    Dependencies | undefined
>(undefined);
VideoSessionDependencyInjectionContext.displayName =
    'VideoSessionDependencyInjectionContext';

type Props = PropsWithChildren<Dependencies>;

export const VideoSessionDependencyInjectionProvider = ({
    children,
    BookSession,
}: Props) => {
    return (
        <VideoSessionDependencyInjectionContext.Provider
            value={{ BookSession }}
        >
            {children}
        </VideoSessionDependencyInjectionContext.Provider>
    );
};

export const useVideoSessionDependencyInjection = createContextHook(
    VideoSessionDependencyInjectionContext,
);
